import { useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { CardCourseDisplay } from '../../courses/components/CardCourseDisplay';
import { Loading } from '../../../components/loading/Loading';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export const TabCoursesHistory = () => {
    const { coursesListByIdUser, loading: loadingCourses } = useSelector((state) => state.options);
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'start',
        loop: false,
        skipSnaps: false,
        dragFree: true,
        containScroll: 'trimSnaps'
    });

    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
    const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const scrollPrev = useCallback(() => {
        if (emblaApi) emblaApi.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);

    const onSelect = useCallback((emblaApi) => {
        setPrevBtnDisabled(!emblaApi.canScrollPrev());
        setNextBtnDisabled(!emblaApi.canScrollNext());
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;

        onSelect(emblaApi);
        emblaApi.on('reInit', onSelect);
        emblaApi.on('select', onSelect);
    }, [emblaApi, onSelect]);

    // Si no hay cursos o está cargando, mostrar sin carrusel
    if (loadingCourses) {
        return (
            <div className="w-full mx-auto">
                <Loading />
            </div>
        );
    }

    if (!coursesListByIdUser.length) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="bg-gray-100 rounded-full p-6 mb-4">
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                </div>
                <h4 className="text-lg font-semibold text-gray-700 mb-2">No hay cursos registrados</h4>
                <p className="text-sm text-gray-500">Aún no se han inscrito cursos para este usuario</p>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {/* Título fijo */}
            <div className="flex-shrink-0 mb-6">
                <div className="bg-white border border-gray-200 rounded-lg px-5 py-2 lg:py-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">
                                    Historial de cursos
                                </h3>
                                <p className="text-xs text-gray-500 font-medium mt-0.5 hidden lg:block">
                                    Lista de cursos en los que estas inscrito.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                            <span className="text-md text-gray-600 font-medium hidden lg:block">Total:</span>
                            <span className="text-md font-bold text-indigo-600">
                                {coursesListByIdUser?.length || 0}
                            </span>
                            <span className="text-md text-gray-600 font-medium">cursos</span>
                        </div>
                    </div>
                </div>
            </div>
            {/* Contenedor con scroll vertical */}
            <div className="flex-1 overflow-y-auto flex flex-col">
                {/* Barra de Control Superior */}
                {coursesListByIdUser.length > 1 && (
                    <div className="flex-shrink-0 flex items-center justify-center gap-6 py-2 px-2">
                        {/* Botón Anterior */}
                        <button
                            className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
                            onClick={scrollPrev}
                            disabled={prevBtnDisabled}
                            aria-label="Curso anterior"
                        >
                            <ChevronLeftIcon className="w-5 h-5 text-white" />
                        </button>

                        {/* Indicador de posición */}
                        <div className="flex items-center gap-2">
                            {coursesListByIdUser.map((_, index) => (
                                <button
                                    key={index}
                                    className={`h-2 rounded-full transition-all duration-300 ${selectedIndex === index
                                        ? 'w-8 bg-indigo-600'
                                        : 'w-2 bg-gray-300 hover:bg-gray-400'
                                        }`}
                                    onClick={() => emblaApi && emblaApi.scrollTo(index)}
                                    aria-label={`Ir al curso ${index + 1}`}
                                />
                            ))}
                        </div>

                        {/* Botón Siguiente */}
                        <button
                            className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
                            onClick={scrollNext}
                            disabled={nextBtnDisabled}
                            aria-label="Siguiente curso"
                        >
                            <ChevronRightIcon className="w-5 h-5 text-white" />
                        </button>
                    </div>
                )}

                {/* Contenedor del Carrusel */}
                <div className="flex-1 overflow-x-hidden overflow-y-auto" ref={emblaRef}>
                    <div className={`flex ${coursesListByIdUser.length > 1 ? 'justify-start' : 'justify-center'} gap-4 p-1 md:p-4`}>
                        {coursesListByIdUser.map(({ id, headerImage, language, path, color, level, status, teacher, fromDate, toDate, days, hours, identifier }) => (
                            <div
                                key={id}
                                className="flex-[0_0_95%] md:flex-[0_0_90%] min-w-0 shrink-0"
                            >
                                <CardCourseDisplay
                                    id={id}
                                    headerImage={headerImage}
                                    language={language}
                                    path={path}
                                    color={color}
                                    level={level}
                                    status={status}
                                    teacher={teacher}
                                    fromDate={fromDate}
                                    toDate={toDate}
                                    days={days}
                                    hours={hours}
                                    identifier={identifier}
                                    action={true}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}